import Checkbox from "@general-ui/checkbox/checkbox";
import Icon, { ICONS, COLORS } from "@general-ui/icon";
import TextInput from "@general-ui/text-input/text";
import WaitingIndicator from "@general-ui/waiting-indicator/waiting-indicator";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { useState, useEffect, useCallback } from "react";
import { StaticVideo, VideoConvertStatus, VideoType } from "types/working-model";
import { useAppDispatch, useTypedSelector } from "store/reducers/use-typed-selector";
import { createUploadJob, getChannelRecordings, getChannelUpVideos } from "store/actions/admin/videos";
import { useGetRecordingUrl } from "hooks/recording-jwt.hooks";
import { OptionalComponent } from "utils/optional-component";
import { showAlert } from "@general-ui/alert/alert-service";
import VideoBackgroundImage from "@general-ui/video-thumbnail/video-background-image";
import ModalComponent from "@general-ui/modal/modal";
import ThumbnailEditor from "./new-static-video-thumbail-editor";
import { UpdateStaticVideo } from "connection/videos";

interface NewStaticVideoSelectorListProps {
	onSelect: (video: StaticVideo) => void;
	selectedVideos?: StaticVideo[];
	multiselect?: boolean;
	videoTypes?: VideoType[];
	forPageModule?: boolean;
}

export default function NewStaticVideoSelectorList({
	onSelect,
	selectedVideos = [],
	multiselect = false,
	videoTypes = [VideoType.uploaded, VideoType.url, VideoType.embed, VideoType.recorded],
	forPageModule = false,
}: NewStaticVideoSelectorListProps): JSX.Element {
	const { user, token } = useTypedSelector(state => state.AuthReducer);
	const { videos, loadingVideos } = useTypedSelector(state => state.VideosReducer);
	const recordedVideos = useTypedSelector(state => state.VideosReducer.recordedVideos);
	const loadingRecordings = useTypedSelector(state => state.VideosReducer.loadingRecordings);
	const [editingThumbnail, setEditingThumbnail] = useState<StaticVideo | null>(null);

	const [visibleVideos, setVisibleVideos] = useState<StaticVideo[]>([]);
	const [searchTerm, setSearchTerm] = useState<string>('');

	const dispatch = useAppDispatch();
	const getRecordingUrl = useGetRecordingUrl();

	useEffect(() => {
		if (token && user) {
			if (!videos.length && videoTypes.includes(VideoType.uploaded)) {
				dispatch(getChannelUpVideos(token, user.active_channel));
			}
			if (!recordedVideos.length && videoTypes.includes(VideoType.recorded)) {
				dispatch(getChannelRecordings(token, user.active_channel));
			}
		}
	}, [dispatch, recordedVideos.length, token, user, videos.length, visibleVideos.length]);

	useEffect(() => {
		let combinedVideos = [...recordedVideos, ...videos];
		
		if (forPageModule) {
			combinedVideos = combinedVideos.filter(video => video.video_id !== null);
		}
		
		if (!searchTerm) {
			setVisibleVideos(combinedVideos);
		} else {
			setVisibleVideos(combinedVideos.filter(video => {
				return video.name.toLowerCase().includes(searchTerm.toLowerCase());
			}));
		}
	}, [videos, searchTerm, recordedVideos, forPageModule]);

	const handleSearch = useCallback(({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(value);
	}, []);

	const handleSelectVideo = (video: StaticVideo) => () => {
		onSelect(video);
	};

	function clickUpload() {
		if (!user || !token) return;
		dispatch(createUploadJob(() => {
			dispatch(getChannelUpVideos(token, user.active_channel));
			showAlert({
				message: "Video Uploaded",
				description: "Your video has finished uploading!",
				duration: 5000
			});
		}, () => {
			showAlert({
				message: "Video Uploading",
				description: "Your video is uploading. You can view its progress at the bottom right of the screen.",
				duration: 5000
			});
		}));
	}
	
	const loading = loadingVideos || loadingRecordings;

	async function handleSaveThumbnail(): Promise<void> {
		if (!editingThumbnail || !token) return;``
		const updatedVideos = [...visibleVideos];
		let video = updatedVideos.find(v => v.uuid === editingThumbnail.uuid);
		if (!video) return;

		video = { ...editingThumbnail, thumbnail_url: editingThumbnail.thumbnail_url, original_image: editingThumbnail.original_image, use_custom_thumbnail: true };

		try {
			await UpdateStaticVideo(token, video);
			setVisibleVideos(updatedVideos);
			setEditingThumbnail(null);
		} catch (error) {
			showAlert({
				message: "Error",
				description: "There was an error saving your video. Please try again.",
				duration: 5000
			});
		}
	}

	function handleThumbnailImageChange(images: { original: string; edited: string; }): void {
		if (!editingThumbnail) return;
		setEditingThumbnail({ ...editingThumbnail, thumbnail_url: images.edited, original_image: images.original, use_custom_thumbnail: true });
	}

	return (
		<>
			<div className="recorded-header">
				<TextInput
					onChange={handleSearch}
					placeholder={'Search video...'}
					icon={ICONS.SEARCH}
					size={'small'}
				/>
				<span>or</span>
				<button className="small" onClick={clickUpload}>Upload video</button>
			</div>
			<div className="recorded-videos">
				<OptionalComponent display={loading}>
					<WaitingIndicator />
				</OptionalComponent>
				<OptionalComponent display={!loading}>
					{/* Convert to virtuoso grid */}
					<>
						{visibleVideos.map(video => {
							const isSelected = selectedVideos.some(v => v.uuid === video.uuid);
							const thumbnailUrl = getRecordingUrl(video.thumbnail_url);
							const thumbnail = thumbnailUrl ? `url(${thumbnailUrl})` : 'none';
							return (
								<div key={video.uuid} className={classNames("recorded-video", { selected: isSelected })}>
									<VideoBackgroundImage onClick={handleSelectVideo(video)} style={{ backgroundImage: thumbnail }} className="video-banner">
										{/* <AnimatePresence> */}
											<>
											{video.convert_status === VideoConvertStatus.progressing && (
												<motion.div
													key={video.uuid}
													initial={{ opacity: 0 }}
													animate={{ opacity: 1 }}
													exit={{ opacity: 0 }}
													className="processing-overlay">
													<span>Your upload is currently processing.<br /><br />Progress: {video.convert_progress ?? 0}%</span>
												</motion.div>
												)}
											</>
										{/* </AnimatePresence> */}
										<div className="video-options">
											<OptionalComponent display={multiselect}>
												<Checkbox
													onChange={handleSelectVideo(video)}
													value={'selectVideo'}
													checked={isSelected ?? false}
													size={20}
													color={isSelected ? COLORS.CYAN : COLORS.WHITE}
													backgroundColor={isSelected ? COLORS.WHITE : undefined}
													selectedIconName={ICONS.CHECKBOX_BORDERLESS}
													unselectedIconName={isSelected ? ICONS.OFF : undefined}
												/>
												<button className="no-style edit-thumbnail" onClick={() => setEditingThumbnail(video)}>
													<Icon name={ICONS.EDIT} color={COLORS.WHITE} size={20} />
												</button>
											</OptionalComponent>
										</div>
									</VideoBackgroundImage>
									<label>{video.name}</label>
									{video.duration_ms && <span className="duration">{new Date(video.duration_ms).toISOString().substr(11, 8)}</span>}
								</div>
							);
						})}
					</>
				</OptionalComponent>
			</div>
			<OptionalComponent display={!!editingThumbnail}>
				<ModalComponent
					title="Edit Thumbnail"
					open={true}
					onRequestClose={() => setEditingThumbnail(null)}
					cancellable={false}
					closeable={false}
					footer={<div className="modal-footer">
						<button className="" onClick={() => setEditingThumbnail(null)}>Cancel</button>
						<button className="lemonade" onClick={handleSaveThumbnail}>Done</button>
					</div>}
				>
					<ThumbnailEditor
						workingVideo={editingThumbnail}
						onImageChange={handleThumbnailImageChange}
					/>
				</ModalComponent>
			</OptionalComponent>
		</>
	);
}